import { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import apiClient from '../services/apiClient';
import { PageHeader, Spinner } from '../components/ui';
import type { ConferenceEvent } from '@conference/shared';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { adminEventService } from '../services/adminEventService';
import { useSearchParams } from 'react-router';

const COLORS = ['#0ea5e9', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#ec4899'];

interface AnalyticsData {
  sessionsByTrack:  { track: string; count: number }[];
  sessionsByType:   { type: string; count: number }[];
  attendeesByRole:  { role: string; count: number }[];
  registrationsByDay: { date: string; count: number }[];
  totals: {
    events: number; sessions: number; speakers: number;
    papers: number; exhibitors: number; attendees: number;
  };
}

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
      <p className="text-xs text-gray-400 mb-1">{label}</p>
      <p className={`text-3xl font-bold ${color}`}>{value.toLocaleString()}</p>
    </div>
  );
}

export default function AnalyticsPage() {
  const { activeWorkspaceId } = useAdminAuth();
  const [searchParams] = useSearchParams();
  const requestedEventId = searchParams.get('event_id') ?? '';
  const [events, setEvents]     = useState<ConferenceEvent[]>([]);
  const [eventId, setEventId]   = useState<string>('');
  const [data, setData]         = useState<AnalyticsData | null>(null);
  const [loading, setLoading]   = useState(false);

  useEffect(() => {
    const list = activeWorkspaceId ? adminEventService.list(activeWorkspaceId) : apiClient.get<ConferenceEvent[]>('/events');
    list.then((data) => {
      setEvents(data);
      const requested = data.find((event) => event.id === requestedEventId);
      if (requested || data[0]) setEventId((requested ?? data[0]).id as string);
    }).catch(() => {});
  }, [activeWorkspaceId, requestedEventId]);

  useEffect(() => {
    if (eventId) loadAnalytics(eventId);
  }, [eventId]);

  async function loadAnalytics(eid: string) {
    setLoading(true);
    try {
      const [sessionsData, attendeesData, analyticsData, allEvents, speakersData] = await Promise.all([
        apiClient.get<{ id: string; type: string; track: string | null }[]>(`/sessions?event_id=${eid}`),
        apiClient.get<{ id: string; role: string; registered_at?: string }[]>(`/admin/attendees?event_id=${eid}`),
        apiClient.get<{ sessions: number; papers: number; exhibitors: number; attendees: number }>(`/admin/analytics?event_id=${eid}`),
        activeWorkspaceId ? adminEventService.list(activeWorkspaceId) : apiClient.get<ConferenceEvent[]>('/events'),
        apiClient.get<{ id: string }[]>(`/speakers?event_id=${eid}`),
      ]);

      // Sessions by track
      const trackMap: Record<string, number> = {};
      sessionsData.forEach((s) => { const t = s.track ?? 'General'; trackMap[t] = (trackMap[t] ?? 0) + 1; });
      const sessionsByTrack = Object.entries(trackMap).map(([track, count]) => ({ track, count })).sort((a, b) => b.count - a.count);

      // Sessions by type
      const typeMap: Record<string, number> = {};
      sessionsData.forEach((s) => { typeMap[s.type] = (typeMap[s.type] ?? 0) + 1; });
      const sessionsByType = Object.entries(typeMap).map(([type, count]) => ({ type: type.charAt(0).toUpperCase() + type.slice(1), count }));

      // Attendees by role
      const roleMap: Record<string, number> = {};
      attendeesData.forEach((a) => { roleMap[a.role] = (roleMap[a.role] ?? 0) + 1; });
      const attendeesByRole = Object.entries(roleMap).map(([role, count]) => ({ role: role.charAt(0).toUpperCase() + role.slice(1), count }));

      // Registrations by day (last 14 days) — use created_at if registered_at unavailable
      const dayMap: Record<string, number> = {};
      attendeesData.forEach((a) => {
        const day = (a.registered_at ?? '').slice(0, 10);
        if (day) dayMap[day] = (dayMap[day] ?? 0) + 1;
      });
      const registrationsByDay = Object.entries(dayMap)
        .sort(([a], [b]) => a.localeCompare(b))
        .slice(-14)
        .map(([date, count]) => ({ date: date.slice(5), count }));

      setData({
        sessionsByTrack, sessionsByType, attendeesByRole, registrationsByDay,
        totals: {
          events:     allEvents.length,
          sessions:   analyticsData.sessions,
          speakers:   speakersData.length,
          papers:     analyticsData.papers,
          exhibitors: analyticsData.exhibitors,
          attendees:  analyticsData.attendees,
        },
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="p-8">
      <PageHeader
        title="Analytics"
        description="Engagement and content metrics for your events."
        action={
          <select
            value={eventId}
            onChange={(e) => setEventId(e.target.value)}
            className="px-3.5 py-2 bg-gray-900 border border-gray-700 rounded-lg text-sm text-gray-100 focus:outline-none focus:ring-2 focus:ring-sky-500/40"
          >
            {events.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        }
      />

      {loading || !data ? (
        <div className="flex justify-center py-20"><Spinner className="w-6 h-6 text-gray-500" /></div>
      ) : (
        <div className="space-y-6">
          {/* Stat cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            <StatCard label="Total Events"    value={data.totals.events}     color="text-white" />
            <StatCard label="Sessions"        value={data.totals.sessions}   color="text-sky-400" />
            <StatCard label="Speakers"        value={data.totals.speakers}   color="text-violet-400" />
            <StatCard label="Papers"          value={data.totals.papers}     color="text-amber-400" />
            <StatCard label="Exhibitors"      value={data.totals.exhibitors} color="text-emerald-400" />
            <StatCard label="Registered"      value={data.totals.attendees}  color="text-pink-400" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Sessions by Track */}
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-white mb-4">Sessions by Track</h3>
              {data.sessionsByTrack.length === 0 ? (
                <p className="text-xs text-gray-500 text-center py-8">No track data</p>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={data.sessionsByTrack} layout="vertical" margin={{ left: 8, right: 16 }}>
                    <XAxis type="number" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="track" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} width={90} />
                    <Tooltip contentStyle={{ background: '#111827', border: '1px solid #374151', borderRadius: 8, fontSize: 12 }} />
                    <Bar dataKey="count" fill="#0ea5e9" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Sessions by Type */}
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-white mb-4">Sessions by Type</h3>
              {data.sessionsByType.length === 0 ? (
                <p className="text-xs text-gray-500 text-center py-8">No session data</p>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={data.sessionsByType} dataKey="count" nameKey="type" cx="50%" cy="50%" outerRadius={75} label={({ type, percent }) => `${type} ${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={11}>
                      {data.sessionsByType.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: '#111827', border: '1px solid #374151', borderRadius: 8, fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Registrations over time */}
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-white mb-4">Registrations Over Time</h3>
              {data.registrationsByDay.length === 0 ? (
                <p className="text-xs text-gray-500 text-center py-8">No registration data</p>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={data.registrationsByDay}>
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ background: '#111827', border: '1px solid #374151', borderRadius: 8, fontSize: 12 }} />
                    <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Attendees by Role */}
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-white mb-4">Attendees by Role</h3>
              {data.attendeesByRole.length === 0 ? (
                <p className="text-xs text-gray-500 text-center py-8">No attendee data</p>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={data.attendeesByRole} dataKey="count" nameKey="role" cx="50%" cy="50%" outerRadius={75}>
                      {data.attendeesByRole.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ background: '#111827', border: '1px solid #374151', borderRadius: 8, fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
